import { FileText, CheckCircle, ExternalLink, DollarSign, Shirt, BookOpen } from 'lucide-react';
import { Button } from '~/components/ui/button';
import { Label } from '~/components/ui/label';
import { Checkbox } from '~/components/ui/checkbox';
import { Separator } from '~/components/ui/separator';

export function PolicyAcknowledgmentsStep({
  policyDocuments,
  invoiceSummary,
  acknowledgments,
  onAcknowledgmentChange
}: any) {
  const handleUniformViewed = () => {
    onAcknowledgmentChange({
      ...acknowledgments,
      uniformPolicyViewed: true,
      uniformPolicyViewedAt: new Date().toISOString()
    });
  };

  const handleTuitionViewed = () => {
    onAcknowledgmentChange({
      ...acknowledgments,
      tuitionFeeScheduleViewed: true,
      tuitionFeeScheduleViewedAt: new Date().toISOString()
    });
  };

  const handleHandbookViewed = () => {
    onAcknowledgmentChange({
      ...acknowledgments,
      handbookViewed: true,
      handbookViewedAt: new Date().toISOString()
    });
  };

  const handleHandbookAcknowledged = (checked: boolean | string) => {
    onAcknowledgmentChange({
      ...acknowledgments,
      handbookAcknowledged: checked
    });
  };

  const handleTuitionAccepted = (checked: boolean | string) => {
    onAcknowledgmentChange({
      ...acknowledgments,
      tuitionAgreementAccepted: checked
    });
  };

  const enrollmentFees = (invoiceSummary || []).filter((inv: any) => inv.key === 'enrollment_fee');
  const tuitionInvoices = (invoiceSummary || []).filter((inv: any) => inv.key !== 'enrollment_fee');
  const totalTuition = tuitionInvoices.reduce((sum: number, inv: any) => sum + inv.amount, 0);
  const totalFees = enrollmentFees.reduce((sum: number, inv: any) => sum + inv.amount, 0);

  const formatCurrency = (cents: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(cents / 100);
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-purple-100 mb-3">
          <FileText className="h-6 w-6 text-purple-600" />
        </div>
        <h2 className="text-xl font-semibold">Policy Acknowledgments</h2>
        <p className="text-muted-foreground">Please review and acknowledge the following policies</p>
      </div>

      {/* Uniform Policy */}
      <div className="p-4 border rounded-lg space-y-3">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            <Shirt className="h-5 w-5 text-muted-foreground mt-0.5" />
            <div>
              <Label className="text-base font-medium">Uniform Policy</Label>
              <p className="text-sm text-muted-foreground mt-1">
                Review the school's dress code and uniform requirements.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {acknowledgments.uniformPolicyViewed ? (
              <span className="text-sm text-green-600 flex items-center gap-1">
                <CheckCircle className="h-4 w-4" />
                Viewed
              </span>
            ) : (
              <Button
                variant="outline"
                size="sm"
                asChild
                onClick={handleUniformViewed}
              >
                <a
                  href={policyDocuments?.uniformPolicyUrl || '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  View Policy
                </a>
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Tuition & Fee Schedule */}
      <div className="p-4 border rounded-lg space-y-3">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            <DollarSign className="h-5 w-5 text-muted-foreground mt-0.5" />
            <div>
              <Label className="text-base font-medium">Tuition & Fee Schedule</Label>
              <p className="text-sm text-muted-foreground mt-1">
                Review the complete tuition and fee schedule for the school year.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {acknowledgments.tuitionFeeScheduleViewed ? (
              <span className="text-sm text-green-600 flex items-center gap-1">
                <CheckCircle className="h-4 w-4" />
                Viewed
              </span>
            ) : (
              <Button
                variant="outline"
                size="sm"
                asChild
                onClick={handleTuitionViewed}
              >
                <a
                  href={policyDocuments?.tuitionFeeScheduleUrl || '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  View Schedule
                </a>
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Handbook */}
      <div className="p-4 border rounded-lg space-y-3">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            <BookOpen className="h-5 w-5 text-muted-foreground mt-0.5" />
            <div>
              <Label className="text-base font-medium">Student Handbook</Label>
              <p className="text-sm text-muted-foreground mt-1">
                Click here to view the handbook. Please review and download a copy for reference.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {acknowledgments.handbookViewed ? (
              <span className="text-sm text-green-600 flex items-center gap-1">
                <CheckCircle className="h-4 w-4" />
                Viewed
              </span>
            ) : (
              <Button
                variant="outline"
                size="sm"
                asChild
                onClick={handleHandbookViewed}
              >
                <a
                  href={policyDocuments?.handbookUrl || '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  View Handbook
                </a>
              </Button>
            )}
          </div>
        </div>
        {acknowledgments.handbookViewed && (
          <div className="mt-3 pt-3 border-t">
            <div className="flex items-start space-x-3">
              <Checkbox
                id="handbook-ack"
                checked={acknowledgments.handbookAcknowledged || false}
                onCheckedChange={handleHandbookAcknowledged}
              />
              <div className="space-y-2">
                <Label htmlFor="handbook-ack" className="font-medium cursor-pointer">
                  Handbook Acknowledgment
                </Label>
                <p className="text-sm text-muted-foreground">
                  I acknowledge that I have been given access to the School Handbook and have had the opportunity to
                  review its contents, including the school's mission, statement of faith, educational philosophy,
                  policies, and expectations for students and families.
                </p>
                <p className="text-sm text-muted-foreground">
                  I understand that enrollment at the school implies agreement to abide by these policies and to
                  support the school's beliefs and practices as described therein.
                </p>
                <p className="text-sm text-muted-foreground">
                  I further understand that questions or disagreements with these positions do not obligate the school
                  to alter its convictions, curriculum, or policies.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      <Separator />

      {/* Payment Summary */}
      <div className="bg-slate-50 p-4 rounded-lg space-y-3">
        <h3 className="font-medium flex items-center gap-2">
          <DollarSign className="h-4 w-4" />
          Your Payment Obligations
        </h3>
        <div className="space-y-2 text-sm">
          {enrollmentFees.length > 0 && (
            <div className="flex justify-between">
              <span>Enrollment Fees</span>
              <span className="font-medium">{formatCurrency(totalFees)}</span>
            </div>
          )}
          {tuitionInvoices.length > 0 && (
            <div className="flex justify-between">
              <span>Tuition ({tuitionInvoices.length} payment{tuitionInvoices.length !== 1 ? 's' : ''})</span>
              <span className="font-medium">{formatCurrency(totalTuition)}</span>
            </div>
          )}
          <Separator className="my-2" />
          <div className="flex justify-between font-medium text-base">
            <span>Total</span>
            <span>{formatCurrency(totalFees + totalTuition)}</span>
          </div>
        </div>
      </div>

      {/* Tuition Agreement Acceptance */}
      <div className="p-4 border-2 border-primary/20 rounded-lg bg-primary/5">
        <div className="flex items-start space-x-3">
          <Checkbox
            id="tuition-accept"
            checked={acknowledgments.tuitionAgreementAccepted || false}
            onCheckedChange={handleTuitionAccepted}
            disabled={
              !acknowledgments.uniformPolicyViewed ||
              !acknowledgments.tuitionFeeScheduleViewed ||
              !acknowledgments.handbookViewed ||
              !acknowledgments.handbookAcknowledged
            }
          />
          <div className="space-y-1">
            <Label htmlFor="tuition-accept" className="font-medium cursor-pointer">
              I Accept the Tuition Agreement
            </Label>
            <p className="text-sm text-muted-foreground">
              I understand I am responsible for the payments listed above according to
              the Tuition & Fee Schedule. I understand that tuition is non-refundable
              and that late payments may incur additional fees.
            </p>
            {(!acknowledgments.uniformPolicyViewed || !acknowledgments.tuitionFeeScheduleViewed || !acknowledgments.handbookViewed || !acknowledgments.handbookAcknowledged) && (
              <p className="text-sm text-amber-600">
                Please view all policy documents and acknowledge the handbook before accepting.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default PolicyAcknowledgmentsStep;
